import { createPortal } from 'react-dom';

import { SelectOption } from '~/shared/utils/types';
import Button from '~/shared/components/ButtonsV2';
import FilterIcon from '~/shared/assets/icons/filter.svg';
import Typography from '~/shared/components/Typography';
import { usePanelBehavior } from '~/shared/utils/usePanelBehavior';
import { ClearFilterBadge } from '~/shared/components/filters/FilterButton';

import { useFilterState } from './hooks';
import FilterPanelHeader from './FilterPanelHeader';
import FilterPanelContent from './FilterPanelContent';

export type GroupReportselectionOption = {
  label: string;
  options: SelectOption[];
};

interface ReportsFiltersSidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResetFilters?: () => void;
  // Applied filters passed from parent
  appliedClusterNameFilters: SelectOption[];
  appliedInstanceFilters: SelectOption[];
  appliedVolumeIdFilters: SelectOption[];
  appliedVolumeTagFilters: SelectOption[];
  appliedAutoscaleState: string[];
  activeFilterCount: number;
  setSidePanelOpen: (open: boolean) => void;
  isSidePanelOpen: boolean;
}

export default function ReportsFiltersSidePanel({
  open,
  onOpenChange,
  onResetFilters,
  appliedClusterNameFilters,
  appliedInstanceFilters,
  appliedVolumeIdFilters,
  appliedVolumeTagFilters,
  appliedAutoscaleState,
  activeFilterCount,
  setSidePanelOpen,
  isSidePanelOpen,
}: ReportsFiltersSidePanelProps) {
  const filterState = useFilterState({
    open,
    appliedClusterNameFilters,
    appliedInstanceFilters,
    appliedVolumeIdFilters,
    appliedVolumeTagFilters,
    appliedAutoscaleState,
  });

  const hasActiveFilters =
    // appliedClusterNameFilters.length > 0 ||
    // appliedInstanceFilters.length > 0 ||
    // appliedVolumeIdFilters.length > 0 ||
    // appliedVolumeTagFilters.length > 0 ||
    // appliedAutoscaleState.length > 0 ||
    filterState.pendingClusterNameFilters.length > 0 ||
    filterState.pendingInstanceFilters.length > 0 ||
    filterState.pendingVolumeIdFilters.length > 0 ||
    filterState.pendingVolumeTagFilters.length > 0 ||
    filterState.pendingAutoscaleState.length > 0;

  const handleClose = () => {
    filterState.applyPendingFilters();
  };

  const { panelRef } = usePanelBehavior(open, onOpenChange, true, undefined, handleClose);

  const handleResetFilters = () => {
    if (onResetFilters) {
      onResetFilters();
    } else {
      filterState.handleResetFilters();
    }
    onOpenChange(false);
  };

  const panel = (
    <div
      ref={panelRef}
      className={`fixed -mt[0.125px] right-0 z-[2000] w-[587px] bg-white border-l border-primary-70 transform transition-transform duration-300 ease-in-out ${
        open ? 'translate-x-0' : 'translate-x-full'
      }`}
      style={{
        top: 0,
        bottom: 0,
        boxShadow: '-15px 86px 57.4px -3px rgba(0, 0, 0, 0.09)',
      }}
    >
      <div className="flex flex-col h-full">
        <FilterPanelHeader
          onClearAll={handleResetFilters}
          hasActiveFilters={hasActiveFilters}
          onClose={() => {
            handleClose();
            onOpenChange(false);
          }}
        />

        <FilterPanelContent
          panelOpen={open}
          pendingAutoscaleState={filterState.pendingAutoscaleState}
          setPendingAutoscaleState={filterState.setPendingAutoscaleState}
          pendingClusterNameFilters={filterState.pendingClusterNameFilters}
          setPendingClusterNameFilters={filterState.setPendingClusterNameFilters}
          pendingInstanceFilters={filterState.pendingInstanceFilters}
          setPendingInstanceFilters={filterState.setPendingInstanceFilters}
          pendingVolumeIdFilters={filterState.pendingVolumeIdFilters}
          setPendingVolumeIdFilters={filterState.setPendingVolumeIdFilters}
          pendingVolumeTagFilters={filterState.pendingVolumeTagFilters}
          setPendingVolumeTagFilters={filterState.setPendingVolumeTagFilters}
        />
      </div>
    </div>
  );

  return (
    <>
      <div className="flex gap-2 items-cente justify-end  w-full max-w-[713px] bg-gree">
        <Button
          size="md-responsive"
          variant="third"
          className="flex gap-1! items-center relative transition-all duration-800 w-auto group"
          leftIcon={
            <FilterIcon
              className={`size-5 aspect-square group-hover:stroke-primary-400  group-hover:stroke-2 ${
                activeFilterCount > 0 ? 'stroke-primary-400 stroke-2 ' : 'stroke-primary-900'
              }`}
            />
          }
          rightIcon={
            activeFilterCount > 0 && (
              <ClearFilterBadge
                count={activeFilterCount}
                handleClick={handleResetFilters}
                title="Clear all filters"
              />
            )
          }
          onClick={() => {
            setSidePanelOpen(!isSidePanelOpen);
          }}
        >
          <Typography variant="p3" color="text-primary-900" className="whitespace-nowrap">
            Filters
          </Typography>
        </Button>
      </div>
      {typeof document !== 'undefined' ? createPortal(panel, document.body) : null}
    </>
  );
}
